import { every, trim } from "lodash-es";
import {
  ReportDesignItemConfig,
  ReportDesignerItemGroupVertical,
  ReportDesignerItemTab,
  ReportDesignItemGrid,
  ReportDesignItemSelectbox
} from "src/app/modules/bootstrap/modules/report/report-design-item";
import { extractSoapTemplateStaffIdsFromTemplate } from "src/app/modules/bootstrap/modules/report/report-signature-support";

const DUMMY_TEXT = "Lorem ipsum dolor sit amet, consectetur adipiscing elit.";
const DUMMY_TEXTAREA =
  "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.\n\nSecond paragraph. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.\n\nThird paragraph. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.";
const DUMMY_DATE = "2024-01-15";
const DUMMY_DATETIME = "2024-01-15T10:30:00";
const DUMMY_CHECK_VALUE = "Si";
const DUMMY_SELECT_OPTION = "Esempio";

export type ReportDesignItemConfigWithValue = ReportDesignItemConfig & {
  value: string;
};

export type BuildTemplateWithDummyDataResult = {
  template: ReportDesignItemConfigWithValue[];
  dataSource: Record<string, unknown>;
};

export function buildTemplateWithDummyData(
  template: ReportDesignItemConfig[]
): BuildTemplateWithDummyDataResult {
  const dataSource: Record<string, unknown> = {};
  const cloned = cloneAndPopulateDummyData(template, dataSource, template);

  return { template: cloned as ReportDesignItemConfigWithValue[], dataSource };
}

function cloneAndPopulateDummyData(
  items: ReportDesignItemConfig[],
  dataSource: Record<string, unknown>,
  rootTemplate: ReportDesignItemConfig[]
): ReportDesignItemConfig[] {
  return items.map((item) => {
    const cloned = {
      ...item,
      config: { ...item.config }
    } as ReportDesignItemConfig & {
      value?: string;
    };

    switch (item.type) {
      case "spacer":
      case "title":
      case "empty":
      case "customer-signature":
      case "custom":
      case "image":
        cloned.value = "";
        break;
      case "group":
      case "group-vertical": {
        const groupConfig = (item as ReportDesignerItemGroupVertical).config;
        cloned.value = "";
        cloned.config = {
          ...cloned.config,
          group: cloneAndPopulateDummyData(
            groupConfig.group || [],
            dataSource,
            rootTemplate
          )
        };
        break;
      }
      case "group-horizontal": {
        const groupConfig = (item as ReportDesignerItemGroupVertical).config;
        cloned.value = "";
        cloned.config = {
          ...cloned.config,
          group: cloneAndPopulateDummyData(
            groupConfig.group || [],
            dataSource,
            rootTemplate
          )
        };
        break;
      }
      case "tab": {
        const tabConfig = (item as ReportDesignerItemTab).config;
        cloned.value = "";
        cloned.config = {
          ...cloned.config,
          tabs: (tabConfig.tabs || []).map((tab) => ({
            ...tab,
            items: cloneAndPopulateDummyData(
              tab.items || [],
              dataSource,
              rootTemplate
            )
          }))
        };
        break;
      }
      case "text": {
        const dataType = (item.config as { dataType?: string }).dataType;
        if (dataType === "date") {
          cloned.value = DUMMY_DATE;
        } else if (dataType === "datetime") {
          cloned.value = DUMMY_DATETIME;
        } else {
          cloned.value = DUMMY_TEXT;
        }
        break;
      }
      case "textarea": {
        cloned.value = DUMMY_TEXTAREA;
        break;
      }
      case "check": {
        cloned.value = DUMMY_CHECK_VALUE;
        break;
      }
      case "selectbox": {
        const options = (item as ReportDesignItemSelectbox).config?.options;
        const firstOption = options
          ? trim(options.split(",")[0] || "")
          : DUMMY_SELECT_OPTION;
        cloned.value = firstOption || DUMMY_SELECT_OPTION;
        break;
      }
      case "checklist": {
        const rows = getCheckListRows(item.config?.rows);
        const columns = getCheckListColumns(item.config?.columns);
        const groups = getCheckListGroups(item.config?.groups);
        const formData: Record<string, boolean> = {};
        if (groups.length > 0 && columns.length > 0 && rows.length > 0) {
          for (const group of groups) {
            for (const column of columns) {
              for (const row of rows) {
                formData[`${group}-${column}-${row}`] = true;
              }
            }
          }
        } else {
          formData["dummy"] = true;
        }
        cloned.value = JSON.stringify(formData);
        break;
      }
      case "table": {
        cloned.value = "";
        const gridConfig = (item as ReportDesignItemGrid).config;
        (gridConfig.items || []).forEach(
          (col: { property: string; dataType: string }) => {
            if (col.property) {
              dataSource[col.property] = getDummyValueForDataType(col.dataType);
            }
          }
        );
        break;
      }
      case "signature": {
        cloned.value = "";
        if ((item.config as { user?: string }).user === "authors") {
          const authorIds =
            extractSoapTemplateStaffIdsFromTemplate(rootTemplate);
          dataSource.authors = authorIds;
          dataSource.staff = authorIds.map((id) => ({ id }));
        }
        break;
      }
      default:
        cloned.value = "";
        break;
    }

    return cloned;
  });
}

function getDummyValueForDataType(dataType: string): unknown {
  switch (dataType) {
    case "date":
      return DUMMY_DATE;
    case "datetime":
      return DUMMY_DATETIME;
    case "boolean":
      return true;
    case "currency":
    case "number":
      return 42;
    case "translated":
      return "translated_dummy";
    default:
      return DUMMY_TEXT;
  }
}

export function getCheckListGroups(value: string | undefined): string[] {
  if (!value) {
    return [""];
  }

  return trim(value)
    .split(",")
    .map((x) => trim(x).replace(/[\s]/g, "_").replace(/[\.]/g, "-"));
}

export function getCheckListRows(value: string | undefined): string[] {
  if (!value) {
    return [];
  }

  return trim(value)
    .split(",")
    .map((x) => trim(x).replace(/[\s]/g, "_").replace(/[\.]/g, "-"));
}

export function getCheckListColumns(value: string | undefined): string[] {
  if (!value) {
    return [];
  }

  return trim(value)
    .split(",")
    .map((x) => trim(x).replace(/[\s]/g, "_").replace(/[\.]/g, "-"));
}

export function isItemEmpty(item: ReportDesignItemConfig): boolean {
  switch (item.type) {
    case "group": {
      return every(item.config.group, (x) => isItemEmpty(x));
    }
    case "check":
    case "image":
    case "textarea":
    case "text": {
      return !((<any>item).value || item.config.value);
    }
    case "spacer":
    case "title": {
      return true;
    }
    default: {
      return false;
    }
  }
}
